export enum QuestionTypes {
    LIVE_EVENT = 'liveEvent',
    PRE_EVENT = 'preEvent',
}

export enum RecommendedActionPriority {
    HIGH = 'High',
    MED = 'Med',
    LOW = 'Low',
    NA = 'N/A',
}

export enum YesNoValue {
    YES = 'Y',
    NO = 'N',
}

// Makes a dictionary with int values for each recommended action priority. NA = 1, Low = 2, Med = 3...
// These are meant for comparison between action priorities (see which priority is higher)
export const RecommendedActionPriorityIntValues: { [key: string]: number } = Object.assign(
    {},
    ...Object.values(RecommendedActionPriority).map((value, index) => ({ [value]: index + 1 }))
);

export enum RecommendedActionEffort {
    NA = 'N/A',
    SHORT = 'Short',
    MEDIUM = 'Medium',
    LONG = 'Long',
}

export interface AssessmentTemplateTypeVersion {
    type: string;
    version?: string;
}

// New assessment types allowed to be created
// This list is prioritized with assessment type on top being most assessments created in past 12 months
export const SUPPORTED_NEW_ASSESSMENT_TYPES_PROD: AssessmentTemplateTypeVersion[] = [
    { type: 'MRA2', version: 'V3.7' },
    { type: 'CMA', version: 'V3.0' },
    { type: 'CCR', version: 'V1.0' },
    { type: 'PSA', version: 'V2.0' },
    { type: 'CFM', version: 'V2.0' },
    { type: 'RA2', version: 'V1.0' },
    { type: 'MODA', version: 'V1.1' },
    { type: 'WAFSAP', version: 'V1.0' },
    { type: 'WAFAnalytics', version: 'V2.0' },
    { type: 'LZAA', version: 'V2.0' },
    { type: 'MF', version: 'V1.2' },
    { type: 'CCMRA', version: 'V1.0' },
    { type: 'ECSA', version: 'V1.0' },
    { type: 'Ops', version: 'V1.0' },
    { type: 'SaaS', version: 'V1.0' },
    { type: 'SWIFT', version: 'V1.0' },
    { type: 'SA', version: 'V1.0' },
    { type: 'GMA', version: 'V1.0' },
    { type: 'DG', version: 'V1.0' },
    { type: 'FSA', version: 'V1.1' },
    { type: 'FSAR', version: 'V1.0' },
    { type: 'FSAW', version: 'V1.0' },
    { type: 'WMA', version: 'V1.0' },
    { type: 'DCA', version: 'V1.0' },
    { type: 'AIML', version: 'V1.0' },
    { type: 'GASAM', version: 'V1.0' },
    { type: 'GASCM', version: 'V2.0' },
    { type: 'LMHA', version: 'V2.0' },
    { type: 'MRA4ISV', version: 'V1.0' },
    { type: 'DMA', version: 'V2.0' },
    { type: 'MLOps', version: 'V1.2' },
    { type: 'FinOps', version: 'V2.0' },
    { type: 'DCE', version: 'V1.0' },
    { type: 'OQR', version: 'V1.0' },
    { type: 'TM', version: 'V1.0' },
    { type: 'SPA', version: 'V1.3' },
    { type: 'VSGenAI', version: 'V1.0' },
    { type: 'MTRA', version: 'V1.0' },
    { type: 'PMPMA', version: 'V1.1' },
    { type: 'EQR', version: 'V1.0' },
    { type: 'DGS', version: 'V1.0' },
    { type: 'AIMLGENAI', version: 'V1.0' },
    { type: 'COA', version: 'V1.0' },
    { type: 'S3A', version: 'V1.0' },
    { type: 'TRD', version: 'V1.2' },
    { type: 'MPBR', version: 'V1.0' },
    { type: 'OMMA', version: 'V1.0' },
    { type: 'SMA', version: 'V1.0' },
    { type: 'RMA', version: 'V2.0' },
    { type: 'AWSMAR', version: 'V1.0' },
    { type: 'CERTMAROLA', version: 'V1.0' },
    { type: 'HCRA', version: 'V1.0' },
    { type: 'SIPES', version: 'V1.0' },
    { type: 'SIPEOP', version: 'V1.0' },
];
export const SUPPORTED_NEW_ASSESSMENT_TYPES_TESTING: AssessmentTemplateTypeVersion[] = SUPPORTED_NEW_ASSESSMENT_TYPES_PROD.concat([
    // assessment types only in testing stages
    { type: 'TEST', version: 'V1.0' },
    { type: 'MRA3a', version: 'V1.0' },
    { type: 'CAFA', version: 'V1.0' },
    { type: 'CAFE', version: 'V1.0' },
    { type: 'EVR', version: 'V1.0' },
    { type: 'LAA', version: 'V1.0' },
    { type: 'LHA', version: 'V1.0' },
    { type: 'ORA', version: 'V1.0' },
    { type: 'PAA', version: 'V1.0' },
    { type: 'WASEC', version: 'V1.2' },
    { type: 'WASEC3', version: 'V1.0' },
    { type: 'DBFA', version: 'V1.1' },
    { type: 'AESA', version: 'V1.0' },
    { type: 'UWA', version: 'V1.0' },
    { type: 'OPENSEARCH', version: 'V1.0' },
    { type: 'CEA', version: 'V1.0' },
    { type: 'CAA', version: 'V1.0' },
    { type: 'SIPEOP', version: 'V1.0' },
    { type: 'SIPES', version: 'V1.0' },
]).map((templateTypeVersion) => {
    switch (templateTypeVersion.type) {
        // For assessment types already deployed to prod, overwrite version for testing stages. For example:
        // case 'CFM':
        //     return { ...templateTypeVersion, version: 'V2.0' };
        default:
        // fall through intended
    }
    return templateTypeVersion;
});

// Assessment types supported for Read/Update
export const SUPPORTED_ASSESSMENT_TYPES_PROD: AssessmentTemplateTypeVersion[] = [
    { type: 'CCMRA' },
    { type: 'CCR' },
    { type: 'CFM' },
    { type: 'CMA' },
    { type: 'ECSA' },
    { type: 'MRA' },
    { type: 'MRA2' },
    { type: 'MF' },
    { type: 'Ops' },
    { type: 'PSA' },
    { type: 'SaaS' },
    { type: 'DevOps' },
    { type: 'SWIFT' },
    { type: 'SA' },
    { type: 'WAFSAP' },
    { type: 'WAFAnalytics' },
    { type: 'LZA' },
    { type: 'GMA' },
    { type: 'DG' },
    { type: 'FSA' },
    { type: 'FSAR' },
    { type: 'FSAW' },
    { type: 'WMA' },
    { type: 'RA' },
    { type: 'RA2' },
    { type: 'MODA' },
    { type: 'DCA' },
    { type: 'AIML' },
    { type: 'GASAM' },
    { type: 'GASCM' },
    { type: 'CSM' },
    { type: 'LMHA' },
    { type: 'MRA4ISV' },
    { type: 'DMA' },
    { type: 'MLOps' },
    { type: 'FinOps' },
    { type: 'DCE' },
    { type: 'OQR' },
    { type: 'TM' },
    { type: 'SPA' },
    { type: 'VSGenAI' },
    { type: 'MTRA' },
    { type: 'PMPMA' },
    { type: 'EQR' },
    { type: 'DGS' },
    { type: 'AIMLGENAI' },
    { type: 'COA' },
    { type: 'S3A' },
    { type: 'TRD' },
    { type: 'MPBR' },
    { type: 'OMMA' },
    { type: 'SMA' },
    { type: 'RMA' },
    { type: 'AWSMAR' },
    { type: 'CERTMAROLA' },
    { type: 'SIP' },
    { type: 'HCRA' },
    { type: 'LZAA' },
    { type: 'SIPEOP' },
    { type: 'SIPES' },
];
export const SUPPORTED_ASSESSMENT_TYPES_TESTING: AssessmentTemplateTypeVersion[] = SUPPORTED_ASSESSMENT_TYPES_PROD.concat([
    { type: 'TEST' },
    { type: 'MRA3a' },
    { type: 'CAFA' },
    { type: 'CAFE' },
    { type: 'EVR' },
    { type: 'LAA' },
    { type: 'LHA' },
    { type: 'ORA' },
    { type: 'PAA' },
    { type: 'WASEC' },
    { type: 'WASEC3' },
    { type: 'DBFA' },
    { type: 'AESA' },
    { type: 'UWA' },
    { type: 'OPENSEARCH' },
    { type: 'CEA' },
    { type: 'CAA' },
]);
